import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTheme } from './theme';
import { ExplainToggle } from './Explain';
import { useLensWeights } from './lensWeights';
import ViewpointDrawer, { viewpointTouched } from './ViewpointDrawer';
import NotificationsBell from './NotificationsBell';
import QuickJump from './QuickJump';

// Workflow-shaped nav (UX rebuild wave 5; front door re-tiered 2026-07-05;
// Add folded into Write 2026-08-07): Home (the reading queue — the lobby) ·
// Write (composition + capture, one door) · Explore (tools + the
// Needs-judgment stream behind one door) · about/explain/viewpoint/bell. The viewpoint drawer rides along here so it exists on
// every page. Colors are CSS vars with the old values as fallbacks (the
// EdgeSentence precedent) so the Reader can re-ink the nav for dark mode
// without touching any other surface.
//
// Mobile (≤640px, 2026-07-25 polish pass): the row compresses to
// Home · bell · ☰ — everything else lives in the ☰ panel. The bell stays
// visible because it is the re-entry loop. Layout classes live in app.css
// (.nav / .nav-desktop / .nav-burger / .nav-sheet).

function Menu({ label, active, items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  return (
    <span ref={ref} style={{ position: 'relative' }}>
      <button className={`nav-pill${active ? ' on' : ''}`} onClick={() => setOpen(!open)} aria-expanded={open}>
        {label} <span className={`wv-chevron${open ? ' down' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <span
          style={{
            position: 'absolute',
            top: '150%',
            left: 0,
            background: 'var(--nav-menu-bg, #fff)',
            border: '1px solid var(--nav-menu-border, #e5e7eb)',
            borderRadius: 'var(--wv-r-md)',
            boxShadow: '0 8px 20px var(--wv-shadow)',
            padding: 'var(--wv-sp-2) 0',
            zIndex: 60,
            minWidth: '160px',
            display: 'block',
          }}
        >
          {items.map(({ to, label: itemLabel }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              className="wv-plainlink"
              style={{ display: 'block', padding: 'var(--wv-sp-2) var(--wv-sp-6)', color: 'var(--nav-menu-ink, #333)', fontSize: 'var(--wv-fs-ui)', whiteSpace: 'nowrap' }}
            >
              {itemLabel}
            </Link>
          ))}
        </span>
      )}
    </span>
  );
}

function BurgerIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

function Nav() {
  const { pathname, search } = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const { weights, engine } = useLensWeights();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const sheetRef = useRef(null);

  // the ☰ panel closes on navigation and on outside taps
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  // the sheet (mobile) keeps flat links; the desktop row uses .nav-pill
  const linkStyle = (path) => ({
    textDecoration: 'none',
    color: pathname === path ? 'var(--nav-ink, #000)' : 'var(--nav-ink-dim, #666)',
    fontWeight: pathname === path ? 'bold' : 'normal',
  });
  const navPill = (path) => `nav-pill${pathname === path ? ' on' : ''}`;

  // A signed-out reader is usually somewhere worth returning to — a shared
  // thread most of all. Carry the current location through the auth pages so
  // signing in is a detour, not an exit.
  const authTo = (base) =>
    pathname === '/' || pathname === '/login' || pathname === '/register'
      ? base
      : `${base}?from=${encodeURIComponent(pathname + search)}`;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const touched = viewpointTouched(weights, engine);

  // "Needs judgment" and "Browse" are DEEP LINKS into the merged front door
  // since 2026-08-07 (HANDOFF 0c), not pages: each is the `everything` pill
  // under one ranking rule. They keep their nav entries deliberately — a rule
  // on a pill row is less discoverable than a named door, and the fold was
  // meant to remove implementations, not the ways in. Whether the daily loop
  // still needs both entries is a question for the complexity sitting, which
  // owns what to hide; this change does not pre-empt it.
  const exploreItems = [
    { to: '/?view=everything', label: 'Needs judgment' },
    { to: '/?view=everything&sort=new', label: 'Browse' },
    { to: '/trust', label: 'Trust atlas' },
    { to: '/trust/inspect', label: 'Inspector' },
    // "Relate" since 2026-08-05: /triage generalized into the pair page
    // (working set + deferred pairs) — the label names the job, not the queue
    ...(user ? [{ to: '/triage', label: 'Relate' }] : []),
    // Threads (2026-08-06): the list is only the way BACK to one — threads are
    // started from an object page or from a walk. Signed-in only, because the
    // list is the viewer's own; a shared thread is reached by its link.
    ...(user ? [{ to: '/threads', label: 'Threads' }] : []),
    // Groups (2026-08-13): audiences you can share to. Signed-in only for the
    // same reason as Threads — the list is the viewer's own.
    ...(user ? [{ to: '/groups', label: 'Groups' }] : []),
  ];

  return (
    <nav className="nav">
      <Link to="/" className={navPill('/')}>Home</Link>
      {/* ONE door for words (DECISIONS.md 2026-08-07 "Write and Add merge"):
          /write is composition AND ingestion — the box tells them apart. It
          lights for /create too, which lives behind its structured-create
          pointer, as it did behind Add's. */}
      {user && (
        <Link
          className={`nav-desktop nav-pill${pathname === '/write' || pathname === '/create' ? ' on' : ''}`}
          to="/write"
        >
          Write
        </Link>
      )}
      {/* Viewsets folded into Home (2026-08-05): the pill row on / is the list,
          ?viewset= is the detail — one nav concept fewer. */}
      {/* '/resolve' and '/browse' are gone from the active list on purpose:
          both now resolve to '/', which is the Home pill's own path, so
          leaving them would light Explore and Home at the same time. */}
      <span className="nav-desktop">
        <Menu
          label="Explore"
          active={['/trust', '/trust/inspect', '/triage', '/thread', '/groups'].some((p) => pathname.startsWith(p))}
          items={exploreItems}
        />
      </span>
      <div className="nav-right">
        <Link className={`nav-desktop ${navPill('/about')}`} to="/about">About</Link>
        <span className="nav-desktop"><ExplainToggle /></span>
        <button
          className="nav-desktop"
          onClick={toggle}
          title="Toggle dark mode"
          aria-label="Toggle dark mode"
          style={{ fontSize: 'var(--wv-fs-md)', cursor: 'pointer', lineHeight: 1.4 }}
        >
          {theme === 'dark' ? '☀' : '☾'}
        </button>
        <button
          className="nav-desktop"
          onClick={() => setDrawerOpen(true)}
          title="Lens weights + trust engine — your blend, applied on every page"
          style={{ fontSize: 'var(--wv-fs-md)', cursor: 'pointer' }}
        >
          Viewpoint{touched ? ' •' : ''}
        </button>
        {user && <NotificationsBell />}
        {user ? (
          <>
            <Link
              className="nav-desktop"
              to="/settings"
              title="Settings — MCP access tokens"
              style={{ fontSize: 'var(--wv-fs-ui)', textDecoration: 'none', color: pathname === '/settings' ? 'var(--nav-ink, #000)' : 'var(--nav-ink-dim, #666)' }}
            >
              {user.username}
            </Link>
            <button className="nav-desktop" onClick={handleLogout} style={{ fontSize: 'var(--wv-fs-md)' }}>Log out</button>
          </>
        ) : (
          <>
            <Link className={`nav-desktop ${navPill('/login')}`} to={authTo('/login')}>Log in</Link>
            <Link className={`nav-desktop ${navPill('/register')}`} to={authTo('/register')}>Register</Link>
          </>
        )}
        <span ref={sheetRef} className="nav-burger-wrap">
          <button
            className="nav-burger"
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <BurgerIcon />
          </button>
          {menuOpen && (
            <div className="nav-sheet">
              {user && <Link to="/write" style={linkStyle('/write')}>Write</Link>}
              <div className="nav-sheet-label">Explore</div>
              {exploreItems.map(({ to, label }) => (
                <Link key={to} to={to} style={linkStyle(to)}>{label}</Link>
              ))}
              <div className="nav-sheet-sep" />
              <Link to="/about" style={linkStyle('/about')}>About</Link>
              <button onClick={() => { setMenuOpen(false); setDrawerOpen(true); }}>
                Viewpoint{touched ? ' •' : ''}
              </button>
              <button onClick={toggle}>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</button>
              <div className="nav-sheet-row"><ExplainToggle /></div>
              <div className="nav-sheet-sep" />
              {user ? (
                <>
                  <Link to="/settings" style={linkStyle('/settings')}>Settings · {user.username}</Link>
                  <button onClick={handleLogout}>Log out</button>
                </>
              ) : (
                <>
                  <Link to={authTo('/login')} style={linkStyle('/login')}>Log in</Link>
                  <Link to={authTo('/register')} style={linkStyle('/register')}>Register</Link>
                </>
              )}
            </div>
          )}
        </span>
      </div>
      <ViewpointDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      {/* `/` anywhere opens quick-jump search — same global-UI home as the drawer */}
      <QuickJump />
    </nav>
  );
}

export default Nav;
